import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import FallbackSpinner from '@/components/FallbackSpinner';
import {
  canAccessEarlyDashboard,
  resolveRouteListPermission,
  resolveRouteWritePermission,
  ROUTES_WITH_DEDICATED_PERMISSION_GUARD,
} from '@/config/navigation-permission-registry';
import { usePermissions } from '@/hooks/usePermissions';
import { recordRouteListGuardDenied, recordUnmappedRouteDenied } from '@/lib/rbac-monitor';

/**
 * Enforces ROUTE_LIST_PERMISSIONS for staff-shell routes. Skips paths that
 * already sit under a dedicated PermissionBasedRoute wrapper. Paths with no
 * registry entry are denied.
 */
export const RouteListPermissionGuard: React.FC = () => {
  const { pathname } = useLocation();
  const { hasPermission, isLoading } = usePermissions();

  const hasDedicatedGuard = ROUTES_WITH_DEDICATED_PERMISSION_GUARD.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
  if (hasDedicatedGuard) {
    return <Outlet />;
  }

  const isEarlyDashboard = pathname === '/app-dashboard' || pathname.startsWith('/app-dashboard/');
  if (isEarlyDashboard) {
    if (isLoading) {
      return <FallbackSpinner />;
    }
    if (!canAccessEarlyDashboard(hasPermission)) {
      recordRouteListGuardDenied('user_analytics', 'list', pathname);
      return <Navigate to="/unauthorized" replace />;
    }
    return <Outlet />;
  }

  const required = resolveRouteListPermission(pathname);
  const writeRequired = resolveRouteWritePermission(pathname);

  if (!required && !writeRequired) {
    recordUnmappedRouteDenied(pathname);
    return <Navigate to="/unauthorized" replace />;
  }

  if (isLoading) {
    return <FallbackSpinner />;
  }

  if (writeRequired && !hasPermission(writeRequired.feature, writeRequired.action)) {
    recordRouteListGuardDenied(writeRequired.feature, writeRequired.action, pathname);
    return <Navigate to="/unauthorized" replace />;
  }

  if (required && !hasPermission(required.feature, required.action)) {
    recordRouteListGuardDenied(required.feature, required.action, pathname);
    return <Navigate to="/unauthorized" replace />;
  }

  return <Outlet />;
};

export default RouteListPermissionGuard;
